import type { ReactNode } from 'react';
import { createStore } from 'zustand/vanilla';

import { onSessionReset } from '@shared/session';

/**
 * 일회성 오버레이(확인 · 알림 모달 등)의 명령형 API.
 *
 *   const ok = await overlay.open<boolean>(({ close }) => (
 *     <ConfirmDialog onConfirm={() => close(true)} onCancel={() => close(false)} />
 *   ));
 *
 * - 오버레이 종류를 모아 둔 중앙 enum 이 없다. 호출부가 그릴 것을 직접 넘긴다.
 * - 닫히면 Promise 가 결과로 끝난다. 결과 없이 닫히면 `undefined`.
 * - 주소로 열고 닫아야 하는 상세 오버레이는 `registerOverlay`(url-overlay)를 쓴다.
 */
export interface OverlayControls<T> {
  id: string;
  close: (result?: T) => void;
}

export type OverlayRender<T> = (controls: OverlayControls<T>) => ReactNode;

export interface OverlayEntry {
  id: string;
  render: () => ReactNode;
  dismiss: () => void;
}

interface OverlayState {
  entries: OverlayEntry[];
}

let sequence = 0;

export const overlayStore = createStore<OverlayState>()(() => ({ entries: [] }));

function remove(id: string) {
  overlayStore.setState((state) => ({
    entries: state.entries.filter((entry) => entry.id !== id),
  }));
}

function open<T = void>(render: OverlayRender<T>): Promise<T | undefined> {
  sequence += 1;
  const id = `overlay-${sequence}`;

  return new Promise<T | undefined>((resolve) => {
    let settled = false;
    const close = (result?: T) => {
      if (settled) return;
      settled = true;
      remove(id);
      resolve(result);
    };
    const entry: OverlayEntry = {
      id,
      render: () => render({ id, close }),
      dismiss: () => close(undefined),
    };
    overlayStore.setState((state) => ({ entries: [...state.entries, entry] }));
  });
}

function closeAll(): void {
  for (const entry of overlayStore.getState().entries) entry.dismiss();
}

export const overlay = { open, closeAll };

onSessionReset(closeAll);
